import React, { useCallback, useEffect, useState } from "react";
import { IssueService, SharedIssue } from "../../js/issues/issueService";
import Accordion, { AccordionPanelProps } from "../../js/components/accordion/Accordion";
import { IssueDetailsProps } from "../../js/headersegment/interfaces";

const IssueDetails: React.FC<IssueDetailsProps> = ({ cancel, issue, size, loadSpinner }) => {
  const [loadingView, setLoadingView] = useState(true);
  const [canViewDetails, setCanViewDetails] = useState(false);
  const [unableToViewDetailsMessage] = useState('Unable to get issue details at this time.');
  const [issueDetails, setIssueDetails] = useState<any>(null);

  const fetchIssueDetails = useCallback(async () => {
    try {
      loadSpinner(true);
      const resp = await IssueService.details(issue.id as any);
      if (resp?.status === 200 && resp?.data?.id) {
        setIssueDetails({ ...resp.data, ...SharedIssue.processIssue(resp.data, true, resp.data.usernames) });
        setCanViewDetails(true);
      }
    } catch (error) {
      loadSpinner(false);
      console.error('Failed to fetch issue details:', error);
    } finally {
      loadSpinner(false);
      setLoadingView(false);
    }
  }, [issue.id]);

  useEffect(() => {
    fetchIssueDetails();

    return () => {
      setIssueDetails(null);
    };
  }, [fetchIssueDetails]);

  const isIssueClosed = () => issueDetails?.state === 'Closed';

  const isIssueAssisting = () => issueDetails?.state === 'Assisting';

  const hasComments = () => !!issueDetails?.comments?.length;

  const hasMessages = () => !!issueDetails?.messages?.length;

  const renderDetailsRow = (displayName: string, displayValue: string) => {
    return (
      <div className="row">
        <div className="col-md-6">
          <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">{displayName}</div>
          <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">{displayName}</div>
          <div className="col-md-6 fs-text">
            <span className="highlight-value fs-text-xs">{displayValue}</span>
          </div>
        </div>
        <div className="col-md-6"></div>
      </div>
    );
  };

  return (
    <>
      <div className={`issue-resolution-modal issue-resolution-modal-details modal-dialog modal-${size || 'md'}`}>
        <div className="title">
          ISSUE DETAILS
          <a onClick={cancel}>
            <i id="modalCancelled" className="cancel pull-right glyphicon icon-white glyphicon-remove"></i>
          </a>
        </div>

        {!loadingView && (!canViewDetails ? (
          <div className="content">
            <div className="choices">
              <div className="row pre-header">
                {unableToViewDetailsMessage}
              </div>
            </div>
          </div>
        ) : (
          <div className="content">
            <div className="choices">
              <div className="row header">
                CURRENT ISSUE STATUS
              </div>
              <div className={`row res-details ${isIssueClosed() ? 'resolved' : 'unresolved'}`}>
                <div className="row sub-header">
                  {issueDetails.titleValue}
                </div>
                <div className="row">
                  <div className="col-md-3 fs-label fs-label-hxs hidden-sm hidden-xs">DESCRIPTION</div>
                  <div className="col-md-3 fs-label fs-label-xs visible-sm visible-xs">DESCRIPTION</div>
                  <div className="col-md-9 fs-text">
                    <span className={`fs-text-xs ${!isIssueClosed() ? 'highlight-value' : ''}`}>{issueDetails.descriptionValue}</span>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">TYPE</div>
                    <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">TYPE</div>
                    <div className="col-md-6 fs-text">
                      <span className={`fs-text-xs ${!isIssueClosed() ? 'highlight-value' : ''}`}>{issueDetails.typeValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">STATUS</div>
                    <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">STATUS</div>
                    <div className="col-md-6 fs-text">
                      <span className={`fs-text-xs ${isIssueAssisting() ? 'highlight-value' : ''}`}>{issueDetails.stateValue}</span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6"></div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">ASSIGNED TO</div>
                    <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">ASSIGNED TO</div>
                    <div className="col-md-6 fs-text">
                      <span className="fs-text-xs">{issueDetails.assignedToUsernameValue}</span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">RESOLUTION</div>
                    <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">RESOLUTION</div>
                    <div className="col-md-6 fs-text">
                      <span className={`fs-text-xs ${isIssueClosed() ? 'highlight-value' : ''}`}>{issueDetails.resolutionValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">SUB RESOLUTION</div>
                    <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">SUB RESOLUTION</div>
                    <div className="col-md-6 fs-text">
                      <span className={`fs-text-xs ${isIssueClosed() ? 'highlight-value' : ''}`}>{issueDetails.subResolutionValue}</span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">OPT-IN TRAVELER</div>
                    <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">OPT-IN TRAVELER</div>
                    <div className="col-md-6 fs-text">
                      <span className="fs-text-xs">{issueDetails.optInTravelerValue}</span>
                    </div>
                  </div>
                  {!isIssueClosed() && (
                    <div className="col-md-6">
                      <div className="col-md-6 fs-label fs-label-hxs hidden-sm hidden-xs">RESOLVE BY</div>
                      <div className="col-md-6 fs-label fs-label-xs visible-sm visible-xs">RESOLVE BY</div>
                      <div className="col-md-6 fs-text">
                        <span className="highlight-value fs-text-xs">{issueDetails.resolveByTimeValue}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-3 fs-label fs-label-hxs hidden-sm hidden-xs">FURTHER DETAILS</div>
                  <div className="col-md-3 fs-label fs-label-xs visible-sm visible-xs">FURTHER DETAILS</div>
                  <div className="col-md-9"></div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Extra Mins Added</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Extra Mins Added</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.extraMinutesAddedValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Overnight Avoided</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Overnight Avoided</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.overnightAvoidedValue}</span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Extra Mins Avoided</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Extra Mins Avoided</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.extraMinutesAvoidedValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Rate Paid</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Rate Paid</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.ratePaidInUsDollarsValue}</span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Traveler Contacted</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Traveler Contacted</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.travelerContactedValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Flights Changed</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Flights Changed</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.flightsChangedValue}</span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Itinerary Updated</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Itinerary Updated</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.itineraryUpdatedValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Hotel Changed</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Hotel Changed</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.hotelChangedValue}</span>
                    </div>
                  </div>
                </div>
                <div className={`row ${!hasComments() && !hasMessages() ? 'bottom-padding' : ''}`}>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">New Flights on Same Carrier</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">New Flights on Same Carrier</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.newFlightsOnSameCarrierValue}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-6 fs-label fs-label-sub fs-label-hxs hidden-sm hidden-xs">Ground Transportation Changed</div>
                    <div className="col-md-6 fs-label fs-label-sub fs-label-xs visible-sm visible-xs">Ground Transportation Changed</div>
                    <div className="col-md-6 fs-text">
                      <span className="highlight-value fs-text-xs">{issueDetails.groundTransportationChangedValue}</span>
                    </div>
                  </div>
                </div>
                {hasComments() && <div className="row comments">
                  <Accordion id={"issueDetailsComment"} className='issue-accordion' panels={[{
                    id: 'parent',
                    title: "ISSUE COMMENTS",
                    show: false,
                    leftIcon: true,
                    children: <div className={`col-md-12 ${!hasMessages() ? 'bottom-padding' : ''}`}>
                      <table className="table table-condensed comments-table">
                        <thead>
                          <tr>
                            <th>Date/Time</th>
                            <th>By</th>
                            <th>Comment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issueDetails.comments.map((comment: any, index: number) => (
                            <tr key={index}>
                              <td className="datetime">{comment.createTimeFormatted}</td>
                              <td className="user">{comment.byUsername}</td>
                              <td className="comment"><pre>{comment.comment}</pre></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  } as AccordionPanelProps]} />
                </div>}
                {hasMessages() && <div className="row comments">
                  <Accordion id={"issueDetailsMessages"} panels={[{
                    id: 'parent',
                    title: "TRAVELER MESSAGES SENT",
                    show: false,
                    children: <div className="col-md-12 bottom-padding">
                      <table className="table table-condensed comments-table">
                        <thead>
                          <tr>
                            <th className="datetime">Date/Time</th>
                            <th className="user">By</th>
                            <th className="comment">To</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issueDetails.messages.map((message: any, index: number) => (
                            <tr key={index}>
                              <td colSpan={3}>
                                <table width="100%">
                                  <tr>
                                    <td className="datetime">{message.createTimeFormatted}</td>
                                    <td className="user">{message.byUsername}</td>
                                    <td className="comment">{message.recipientAddress}</td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td className="comment" colSpan={2}>
                                      <pre>{`" ${message.messageContent} "`}</pre>
                                    </td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  } as AccordionPanelProps]} />
                </div>}
              </div>
            </div>
            <div className="choices">
              <div className="row header">
                ISSUE TIMELINE
              </div>
              <div className="row res-timeline">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th className="datetime">Date/Time</th>
                      <th className="type">Type</th>
                      <th className="detail">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueDetails.timeline.map((event: any, index: number) => (
                      <tr key={index}>
                        <td colSpan={3}>
                          <table>
                            <tr>
                              <td className="datetime">{event.createTimeFormatted}</td>
                              <td className="type">{event.typeValue}</td>
                              <td className="detail">
                                <span>{event.detailTitle}</span>
                                {event.comment && (
                                  <div>
                                    <span className="comment"><pre>"{event.comment}"</pre></span>
                                  </div>
                                )}
                                {event.messageContent && (
                                  <div>
                                    <span className="comment"><pre>"{event.messageContent}"</pre></span>
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="res-details timeline-res-details" colSpan={3}>
                                {event.showResolvedDetails && <Accordion id={"issueDetailsResolve"} panels={[{
                                  id: 'parent',
                                  title: "DETAILS",
                                  show: false,
                                  children: <>
                                    {renderDetailsRow('STATUS', event.resolvedState)}
                                    {renderDetailsRow('RESOLUTION', event.resolvedPrettyType)}
                                    {renderDetailsRow('SUB RESOLUTION', event.resolvedSubResolution)}
                                    {renderDetailsRow('OPT-IN TRAVELER', event.resolvedOptInTraveler)}
                                    {renderDetailsRow('RESOLVED AT', event.resolvedResolveByTime)}
                                    {renderDetailsRow('FURTHER DETAILS', '')}
                                    {renderDetailsRow('Extra Mins Added', event.resolvedExtraMinutesAdded)}
                                    {renderDetailsRow('Overnight Avoided', event.resolvedOvernightAvoided)}
                                    {renderDetailsRow('Extra Mins Avoided', event.resolvedExtraMinutesAvoided)}
                                    {renderDetailsRow('Rate Paid', event.resolvedRatePaidInUsDollars)}
                                    {renderDetailsRow('Traveler Contacted', event.resolvedTravelerContacted)}
                                    {renderDetailsRow('Flights Changed', event.resolvedFlightsChanged)}
                                    {renderDetailsRow('Itinerary Updated', event.resolvedItineraryUpdated)}
                                    {renderDetailsRow('Hotel Changed', event.resolvedHotelChanged)}
                                    {renderDetailsRow('New Flights on Same Carrier', event.resolvedNewFlightsOnSameCarrier)}
                                    {renderDetailsRow('Ground Transportation Changed', event.resolvedGroundTransportationChanged)}
                                  </>
                                } as AccordionPanelProps]} />}
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default IssueDetails;